import React, { useEffect, useState, useContext } from 'react';
import { auth } from '../../config/firebase';
import FullPageLoader from '../../components/ui/progress/fullPageLoader';
import { onAuthStateChanged } from 'firebase/auth';
import { getSignedInUser } from '../../services/user';
import store from '../../app/store';
import {
  addWorkspaceMember as addWorkspaceMemberAction,
  createWorkspace as createWorkspaceAction,
  createInitialWorkspace as createInitialWorkspaceAction,
  getWorkspaces,
  removeWorkspaceMember as removeWorkspaceMemberAction,
  renameWorkspace as renameWorkspaceAction,
  setActiveWorkspace as setActiveWorkspaceAction,
  updateWorkspaceMemberRole as updateWorkspaceMemberRoleAction
} from '../../features/workspace/workspaceSlice';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [workspaceRequired, setWorkspaceRequired] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, initUser);

    return unsubscribe;
  }, []);

  const dispatchWorkspace = (action) => store.dispatch(action).unwrap();

  const refreshCurrentUserProfile = async () => {
    if (!currentUser?.uid) {
      return null;
    }

    const userProfile = await getSignedInUser(currentUser.uid);
    const profileWithId = { id: currentUser.uid, ...userProfile };
    setCurrentUserProfile(profileWithId);
    return profileWithId;
  };

  const initUser = async (user) => {
    if (user) {
      const userProflie = await getSignedInUser(user.uid);
      const profileWithId = { id: user.uid, ...userProflie };

      setCurrentUserProfile(profileWithId);
      setCurrentUser({ ...user });
      setAuthenticated(true);

      const selectedWorkspaceId =
        userProflie?.selectedWorkspaceId || userProflie?.defaultWorkspaceId || null;
      if (selectedWorkspaceId) {
        window.localStorage.setItem('activeWorkspaceId', selectedWorkspaceId);
      } else {
        // No workspace on the profile yet. Clear any stale id so the API can
        // provision and resolve the user's default workspace server-side.
        window.localStorage.removeItem('activeWorkspaceId');
      }

      try {
        const workspacesResponse = await dispatchWorkspace(getWorkspaces());
        setWorkspaces(workspacesResponse);

        const selectedWorkspace = workspacesResponse.find((ws) => ws.id === selectedWorkspaceId);
        const resolvedWorkspace = selectedWorkspace || workspacesResponse[0] || null;
        setCurrentWorkspace(resolvedWorkspace);
        setWorkspaceRequired(!resolvedWorkspace);

        if (resolvedWorkspace?.id) {
          window.localStorage.setItem('activeWorkspaceId', resolvedWorkspace.id);
          setCurrentUserProfile((prev) => ({
            ...prev,
            selectedWorkspaceId: resolvedWorkspace.id,
            workspaceIds: prev?.workspaceIds?.includes(resolvedWorkspace.id)
              ? prev.workspaceIds
              : [...(prev?.workspaceIds || []), resolvedWorkspace.id]
          }));
        }
      } catch (error) {
        setWorkspaces([]);
        setCurrentWorkspace(null);
        setWorkspaceRequired(true);
        window.localStorage.removeItem('activeWorkspaceId');
      }
    } else {
      setAuthenticated(false);
      setCurrentUser(null);
      setCurrentUserProfile(null);
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setWorkspaceRequired(false);
      window.localStorage.removeItem('activeWorkspaceId');
    }

    setLoading(false);
  };

  const refreshWorkspaces = async (preferredWorkspaceId = null) => {
    const workspacesResponse = await dispatchWorkspace(getWorkspaces());
    setWorkspaces(workspacesResponse);

    const targetWorkspaceId = preferredWorkspaceId || currentUserProfile?.selectedWorkspaceId;
    const selectedWorkspace = targetWorkspaceId
      ? workspacesResponse.find((ws) => ws.id === targetWorkspaceId)
      : null;
    setCurrentWorkspace(selectedWorkspace || workspacesResponse[0] || null);

    setWorkspaceRequired(workspacesResponse.length === 0);

    return workspacesResponse;
  };

  const switchWorkspace = async (workspaceId) => {
    if (!workspaceId) {
      return null;
    }

    const workspace = await dispatchWorkspace(setActiveWorkspaceAction(workspaceId));
    window.localStorage.setItem('activeWorkspaceId', workspace.id);

    setCurrentUserProfile((prev) => ({
      ...prev,
      selectedWorkspaceId: workspace.id,
      workspaceIds: prev?.workspaceIds?.includes(workspace.id)
        ? prev.workspaceIds
        : [...(prev?.workspaceIds || []), workspace.id]
    }));

    await refreshWorkspaces(workspace.id);

    return workspace;
  };

  const renameCurrentWorkspace = async (name) => {
    if (!currentWorkspace?.id) {
      return null;
    }

    const workspace = await dispatchWorkspace(
      renameWorkspaceAction({ workspaceId: currentWorkspace.id, name })
    );
    await refreshWorkspaces();
    setCurrentWorkspace(workspace);
    return workspace;
  };

  const addWorkspaceMember = async (member, role, inviteName, inviteEmail) => {
    if (!currentWorkspace?.id) {
      return null;
    }

    const workspace = await dispatchWorkspace(
      addWorkspaceMemberAction({
        workspaceId: currentWorkspace.id,
        member,
        role,
        inviteName,
        inviteEmail
      })
    );
    await refreshWorkspaces();
    return workspace;
  };

  const removeWorkspaceMember = async (memberId, email, inviteId, pending = false) => {
    if (!currentWorkspace?.id) {
      return null;
    }

    const result = await dispatchWorkspace(
      removeWorkspaceMemberAction({
        workspaceId: currentWorkspace.id,
        memberId,
        email,
        inviteId,
        pending
      })
    );
    await refreshWorkspaces();
    return result;
  };

  const updateWorkspaceMemberRole = async (memberId, role) => {
    if (!currentWorkspace?.id) {
      return null;
    }

    const result = await dispatchWorkspace(
      updateWorkspaceMemberRoleAction({
        workspaceId: currentWorkspace.id,
        memberId,
        role
      })
    );
    await refreshWorkspaces();
    return result;
  };

  const createInitialWorkspace = async (name) => {
    const workspace = await dispatchWorkspace(createInitialWorkspaceAction(name));
    window.localStorage.setItem('activeWorkspaceId', workspace.id);

    setCurrentUserProfile((prev) => ({
      ...prev,
      selectedWorkspaceId: workspace.id,
      defaultWorkspaceId: prev?.defaultWorkspaceId || workspace.id,
      workspaceIds: prev?.workspaceIds?.includes(workspace.id)
        ? prev.workspaceIds
        : [...(prev?.workspaceIds || []), workspace.id]
    }));

    await refreshWorkspaces(workspace.id);
    setWorkspaceRequired(false);
    return workspace;
  };

  const createWorkspace = async (name) => {
    const workspace = await dispatchWorkspace(createWorkspaceAction(name));
    window.localStorage.setItem('activeWorkspaceId', workspace.id);

    setCurrentUserProfile((prev) => ({
      ...prev,
      selectedWorkspaceId: workspace.id,
      defaultWorkspaceId: prev?.defaultWorkspaceId || workspace.id,
      workspaceIds: prev?.workspaceIds?.includes(workspace.id)
        ? prev.workspaceIds
        : [...(prev?.workspaceIds || []), workspace.id]
    }));

    await refreshWorkspaces(workspace.id);
    setWorkspaceRequired(false);
    return workspace;
  };

  const value = {
    currentUserProfile,
    currentUser,
    authenticated,
    loading,
    workspaces,
    currentWorkspace,
    workspaceRequired,
    switchWorkspace,
    renameCurrentWorkspace,
    addWorkspaceMember,
    updateWorkspaceMemberRole,
    removeWorkspaceMember,
    refreshWorkspaces,
    createInitialWorkspace,
    createWorkspace,
    refreshCurrentUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <FullPageLoader /> : children}
    </AuthContext.Provider>
  );
};

export { AuthProvider };
